import { useMutation } from '@apollo/client'
import { Box, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OutlinedCross } from '../../../assets/icons'
import { REQUEST_COST_COVERAGE } from '../../../graphql/mutations'
import { FileType } from '../../../utils/fixtures'
import { useNotification } from '../../../utils/hooks'
import { Button, Input, DatePicker } from '../../atoms'
import Uploader from '../Uploader'

interface IDialogContent {
  title: string
  subtitle: string
  message: string
  variables: any
  callback(variables?: any): void
}

interface Attributes {
  address: string
  birthDate?: string
  claimNumber: string
  claimDate: string
  policyNumber?: string
  disputeValue?: string
  legalArea: string
  mobileNumber?: string
  name: string
  phoneNumber?: string
  comments?: string
  commentsLegalAdvice?: string
  contactPersonName?: string
  contactPersonPhoneNumber?: string
  contactPersonMobileNumber?: string
  relationToPolicyholder?: string
  conflictPartnerName?: string
  conflictPartnerBirthDate?: string
  conflictPartnerAddress?: string
  conflictPartnerPhoneNumber?: string
  conflictPartnerMobileNumber?: string
}

interface RequestCoverageButtonProps {
  options?: any
  label: string
  value?: any[]
  disabled?: boolean
  dialogContent?: IDialogContent
  helperText?: string
}

const RequestCoverageButton: React.FC<RequestCoverageButtonProps> = ({
  label,
  value = [],
  disabled = false,
  options = undefined,
  dialogContent = undefined,
  helperText = undefined
}) => {
  const { t } = useTranslation(['common', 'requestCoverage'])
  const { setNotification } = useNotification()
  const [attributes, setAttributes] = useState<Attributes>(dialogContent?.variables)
  useEffect(() => {
    setAttributes(dialogContent?.variables)
  }, [dialogContent?.variables, dialogContent?.variables?.legalArea])
  const [open, setOpen] = useState(false)
  const [files, setFiles] = useState<FileType[]>([])

  const handleFilesChanged = useCallback((newFiles: FileType[] | null | undefined) => {
    setFiles(newFiles || [])
  }, [])

  const handleFileDeleted = useCallback((file: FileType) => {
    setFiles(prev => prev.filter(f => f.signed_id !== file.signed_id))
  }, [])

  const isFormValid = useCallback(() => {
    let valid = true
    if (!(attributes.name && attributes.name.length > 0)) {
      valid = false
    }
    if (!(attributes.address && attributes.address.length > 0)) {
      valid = false
    }
    if (!(attributes.commentsLegalAdvice && attributes.commentsLegalAdvice.length > 0)) {
      valid = false
    }
    // Require either policyNumber OR claimNumber (at least one)
    if (
      !(
        (attributes.policyNumber && attributes.policyNumber.length > 0) ||
        (attributes.claimNumber && attributes.claimNumber.length > 0)
      )
    ) {
      valid = false
    }
    if (!(attributes.claimDate && attributes.claimDate.length > 0)) {
      valid = false
    }
    if (!(attributes.legalArea && attributes.legalArea.length > 0)) {
      valid = false
    }

    return valid
  }, [
    attributes?.name,
    attributes?.address,
    attributes?.commentsLegalAdvice,
    attributes?.policyNumber,
    attributes?.claimNumber,
    attributes?.claimDate,
    attributes?.legalArea
  ])

  const [formValid, setFormValid] = useState(isFormValid())
  useEffect(() => {
    setFormValid(isFormValid())
  }, [isFormValid])

  const [submitMutation] = useMutation(REQUEST_COST_COVERAGE, {
    onCompleted: () => {
      setOpen(false)
      setNotification({
        open: true,
        variant: 'success',
        title: t('success'),
        message: t('requestCoverage:success')
      })
      dialogContent?.callback?.(attributes)
    },
    onError: error => {
      setOpen(false)
      setNotification({ open: true, variant: 'error', title: t('error'), message: error?.message })
    }
  })
  return (
    <>
      <Button color="primary" onClick={() => setOpen(true)} fullWidth disabled={disabled}>
        {label}
      </Button>

      {helperText && (
        <Typography
          variant="caption"
          sx={{ paddingTop: 1, display: 'flex', margin: '0px 14px', width: '100%' }}
        >
          {helperText}
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ display: 'flex', justifyContent: 'center', width: '100%', paddingTop: '5px' }}
      >
        {value?.[0]}
      </Typography>
      <Dialog open={open} maxWidth="lg" data-testid="sms-modal">
        <DialogContent>
          <>
            <Typography variant="h5">{dialogContent?.title}</Typography>
            <Typography variant="caption" sx={{ pt: 1 }}>
              {dialogContent?.subtitle}
            </Typography>
            <Grid container sx={{ mt: 1 }} spacing={2}>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.policyNumber}
                  label={t('caseProperty:policyNumber')}
                  fullWidth
                  onChange={({ target: { value: policyNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, policyNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.claimNumber}
                  label={t('caseProperty:claimNumber')}
                  fullWidth
                  onChange={({ target: { value: claimNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, claimNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <DatePicker
                  value={attributes?.claimDate ? new Date(attributes.claimDate) : null}
                  label={t('caseProperty:claimDate')}
                  type="date"
                  fullWidth
                  onChange={(date: Date | null) =>
                    setAttributes((vars: any) => ({
                      ...vars,
                      claimDate: date ? date.toISOString().split('T')[0] : ''
                    }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Typography variant="h6">{t('caseProperty:policyholder')}</Typography>
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.name}
                  label={t('requestCoverage:name')}
                  required
                  fullWidth
                  onChange={({ target: { value: name } }) =>
                    setAttributes((vars: any) => ({ ...vars, name }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <DatePicker
                  value={attributes?.birthDate ? new Date(attributes.birthDate) : null}
                  label={t('caseProperty:birthDate')}
                  type="date"
                  fullWidth
                  onChange={(date: Date | null) =>
                    setAttributes((vars: any) => ({
                      ...vars,
                      birthDate: date ? moment(date).format('YYYY-MM-DD') : ''
                    }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.address}
                  label={t('requestCoverage:address')}
                  required
                  fullWidth
                  onChange={({ target: { value: address } }) =>
                    setAttributes((vars: any) => ({ ...vars, address }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.phoneNumber}
                  label={t('caseProperty:phoneNumber')}
                  fullWidth
                  onChange={({ target: { value: phoneNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, phoneNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.mobileNumber}
                  label={t('caseProperty:mobileNumber')}
                  fullWidth
                  onChange={({ target: { value: mobileNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, mobileNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Typography variant="h6">{t('caseProperty:contactPerson')}</Typography>
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.contactPersonName}
                  label={t('requestCoverage:contactPersonName')}
                  fullWidth
                  onChange={({ target: { value: contactPersonName } }) =>
                    setAttributes((vars: any) => ({ ...vars, contactPersonName }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.relationToPolicyholder}
                  label={t('requestCoverage:relationToPolicyholder')}
                  fullWidth
                  onChange={({ target: { value: relationToPolicyholder } }) =>
                    setAttributes((vars: any) => ({ ...vars, relationToPolicyholder }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.contactPersonPhoneNumber}
                  label={t('caseProperty:phoneNumber')}
                  fullWidth
                  onChange={({ target: { value: contactPersonPhoneNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, contactPersonPhoneNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.contactPersonMobileNumber}
                  label={t('caseProperty:mobileNumber')}
                  fullWidth
                  onChange={({ target: { value: contactPersonMobileNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, contactPersonMobileNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Typography variant="h6">{t('caseProperty:conflictPartner')}</Typography>
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.conflictPartnerName}
                  label={t('requestCoverage:name')}
                  fullWidth
                  onChange={({ target: { value: conflictPartnerName } }) =>
                    setAttributes((vars: any) => ({ ...vars, conflictPartnerName }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <DatePicker
                  value={
                    attributes?.conflictPartnerBirthDate
                      ? new Date(attributes.conflictPartnerBirthDate)
                      : null
                  }
                  label={t('caseProperty:birthDate')}
                  type="date"
                  fullWidth
                  onChange={(date: Date | null) =>
                    setAttributes((vars: any) => ({
                      ...vars,
                      conflictPartnerBirthDate: date ? moment(date).format('YYYY-MM-DD') : ''
                    }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.conflictPartnerAddress}
                  label={t('requestCoverage:address')}
                  fullWidth
                  onChange={({ target: { value: conflictPartnerAddress } }) =>
                    setAttributes((vars: any) => ({ ...vars, conflictPartnerAddress }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.conflictPartnerPhoneNumber}
                  label={t('caseProperty:phoneNumber')}
                  fullWidth
                  onChange={({ target: { value: conflictPartnerPhoneNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, conflictPartnerPhoneNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.conflictPartnerMobileNumber}
                  label={t('caseProperty:mobileNumber')}
                  fullWidth
                  onChange={({ target: { value: conflictPartnerMobileNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, conflictPartnerMobileNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Typography variant="h6">{t('caseProperty:caseInformations')}</Typography>
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.legalArea}
                  label={t('requestCoverage:legalArea')}
                  disabled
                  required
                  fullWidth
                  onChange={({ target: { value: legalArea } }) =>
                    setAttributes((vars: any) => ({ ...vars, legalArea }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  type="money"
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.disputeValue}
                  label={t('caseProperty:disputeValue')}
                  fullWidth
                  onChange={({ target: { value: disputeValue } }) =>
                    setAttributes((vars: any) => ({ ...vars, disputeValue }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.comments}
                  label={t('caseProperty:caseComments')}
                  multiline
                  minRows={5}
                  fullWidth
                  onChange={({ target: { value: comments } }) =>
                    setAttributes((vars: any) => ({ ...vars, comments }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.commentsLegalAdvice}
                  label={t('caseProperty:caseCommentsLegalAdvice')}
                  required
                  multiline
                  minRows={5}
                  fullWidth
                  onChange={({ target: { value: commentsLegalAdvice } }) =>
                    setAttributes((vars: any) => ({ ...vars, commentsLegalAdvice }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Uploader
                  multiple
                  label={t('requestCoverage:attachments')}
                  onFilesChanged={handleFilesChanged}
                  onFileDeleted={handleFileDeleted}
                />
              </Grid>
            </Grid>
          </>
        </DialogContent>
        <DialogActions>
          <Box sx={{ marginRight: '39px', marginBottom: '42px' }}>
            <Button
              color="secondary"
              sx={{ marginRight: '15px' }}
              onClick={() => setOpen(false)}
              startIcon={<OutlinedCross />}
            >
              {t('common:abort')}
            </Button>
            <Button
              color="primary"
              disabled={!formValid}
              onClick={() => {
                if (formValid) {
                  submitMutation({
                    variables: {
                      ...options?.variables,
                      attributes,
                      attachmentBlobIds: files
                        .map(f => f.signed_id)
                        .filter((id): id is string => Boolean(id))
                    }
                  })
                }
              }}
            >
              {t('sms:sendNow')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RequestCoverageButton
